import { Check, ExternalLink, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { platformOpen } from "@/lib/platformOpen";

// Botão 1 do fluxo "dois botões" (task 96bc7d74): o usuário cria o avatar na
// CONTA DELE no site de consumidor da MetaPerson e paga o próprio export.
// ⛔ NÃO usa o Creator embutido e NÃO chama metaperson-token — a credencial
// Enterprise morre com a fatura de 25/08; este caminho não depende dela.
export const METAPERSON_SITE_URL = "https://metaperson.avatarsdk.com";

// Configuração EXATA do export, medida em 24/08 contra dois arquivos reais
// (tasks 04acc0b1 / 528675c2). Os NOMES das caixas ficam em inglês em todos os
// locales de propósito — é como o site as mostra; só a instrução traduz.
// `kind` decide o ícone (do = marcar/valor ✅ · dont = deixar desmarcado ⛔);
// `emphasis` destaca a linha cuja omissão deixa o avatar MUDO.
const EXPORT_SETTINGS: ReadonlyArray<{
  key: string;
  kind: "do" | "dont";
  emphasis?: boolean;
}> = [
  { key: "metaperson.external.set.lod", kind: "do" },
  { key: "metaperson.external.set.format", kind: "do" },
  { key: "metaperson.external.set.textures", kind: "do" },
  { key: "metaperson.external.set.visemes", kind: "do", emphasis: true },
  { key: "metaperson.external.set.headShape", kind: "dont" },
  { key: "metaperson.external.set.bodyShape", kind: "dont" },
  { key: "metaperson.external.set.headOnly", kind: "dont" },
  { key: "metaperson.external.set.neckCut", kind: "do" },
  { key: "metaperson.external.set.cornea", kind: "dont" },
  { key: "metaperson.external.set.commercial", kind: "do" },
];

interface MetaPersonExternalCreateProps {
  onBack: () => void;
}

export default function MetaPersonExternalCreate({
  onBack,
}: MetaPersonExternalCreateProps) {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col gap-4 w-full">
      <p className="text-[13px] text-muted-foreground leading-relaxed">
        {t("metaperson.external.instructions.intro")}
      </p>

      <div
        className="rounded-2xl p-4 flex flex-col gap-2.5"
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "0.5px solid rgba(255,255,255,0.08)",
        }}
      >
        <p className="font-display text-[12px] font-semibold tracking-wide text-foreground/85">
          {t("metaperson.external.instructions.settingsTitle")}
        </p>
        <ul className="flex flex-col gap-2">
          {EXPORT_SETTINGS.map(({ key, kind, emphasis }) => (
            <li
              key={key}
              className="flex items-start gap-2.5 rounded-lg"
              style={
                emphasis
                  ? {
                      background: "rgba(251,191,36,0.08)",
                      border: "0.5px solid rgba(251,191,36,0.35)",
                      padding: "6px 8px",
                      margin: "0 -8px",
                    }
                  : undefined
              }
            >
              {kind === "do" ? (
                <Check
                  className="w-4 h-4 shrink-0 mt-[1px]"
                  style={{ color: emphasis ? "rgb(251,191,36)" : "rgb(52,211,153)" }}
                />
              ) : (
                <X className="w-4 h-4 shrink-0 mt-[1px] text-red-400/80" />
              )}
              <span
                className="text-[12px] leading-snug"
                style={{
                  color: emphasis
                    ? "rgba(254,243,199,0.95)"
                    : "rgba(255,255,255,0.75)",
                }}
              >
                {t(key)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-[12px] text-muted-foreground leading-relaxed">
        {t("metaperson.external.instructions.keepFile")}
      </p>

      <button
        type="button"
        onClick={() => platformOpen(METAPERSON_SITE_URL, { newTab: true })}
        className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-display font-semibold text-sm tracking-wide transition-all hover:opacity-90 active:scale-[0.98] inline-flex items-center justify-center gap-2"
      >
        <ExternalLink className="w-4 h-4" />
        {t("metaperson.external.instructions.openCta")}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="text-center text-xs text-muted-foreground/50 py-2"
      >
        {t("common.back")}
      </button>
    </div>
  );
}
